import { useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import {
  projectsFlag,
  projectsWebFlag,
  workLetterArtifactFlag,
} from '@heidi/agents/flags';
import { useBooleanFlag, useBooleanFlagLite } from '@heidi/runtime/feature-flags';
import { isTauri } from '@heidi/runtime/platform';
import { HeidiVersion } from '@heidi/schema/ml-scribe';
import { useIsV2HeidiSuggestsFlagEnabled } from '@heidi/session/flags';

/**
 * Billing version for the Heidi 1 hide.
 *
 * Bound in `src/composition/hosts/commerce/commerce-data-wiring.ts`, not imported here. This
 * directory is on the tauri-boot graph, and a domain `/data` door is refused
 * on it. Unresolved (the default, until that wiring runs) does not hide Work.
 */
type HeidiWorkIdentityReads = {
  useHeidiVersion: () => HeidiVersion | null | undefined;
};

const unresolvedIdentityReads: HeidiWorkIdentityReads = {
  useHeidiVersion: () => undefined,
};

let identityReads: HeidiWorkIdentityReads = unresolvedIdentityReads;

export const configureHeidiWorkIdentityReads = (reads: HeidiWorkIdentityReads): void => {
  identityReads = reads;
};

const useHeidiVersion = (): HeidiVersion | null | undefined =>
  identityReads.useHeidiVersion();

export const HEIDI_II_MASTER_FLAG_KEY = 'release-enable-heidi-ii';

/** PostHog rollout for the Heidi 2 opt-in cohort. One mint; the barrel getter reads this. */
export const HEIDI_2_OPT_IN_FLAG_KEY = 'release-heidi-2-opt-in';

/** PostHog child of the Work umbrella. Sidebar IA (MUL-3702/3703). */
export const SIDEBAR_IA_FLAG_KEY = 'release-enable-sidebar-ia';

/**
 * The Heidi Work umbrella: PostHog `release-enable-heidi-ii` OR the legacy
 * Flagsmith Projects keys (`release-scribe-enable-projects` on desktop,
 * `release-scribe-enable-projects-web` on web). Every read runs
 * unconditionally to keep hook order stable.
 *
 * The single Work gate on the frontend: every Work-scoped getter below and the
 * `@heidi/agents/data` inbox gate (via `configureAgentsData`) follow it, so a
 * PostHog-only user never sees Work without its routines or inbox.
 *
 * Turning Work off takes BOTH providers: PostHog `release-enable-heidi-ii` off
 * AND the platform's Flagsmith Projects key off. Flipping one leaves every
 * surface above live for users the other still enables.
 *
 * One extra hide, and only that cohort: a user still on `heidi_version` 1
 * while `release-heidi-2-opt-in` is on — admins included, not only the
 * request-access strip. Unknown billing (still loading, or persisted from a
 * previous session) does not hide it.
 *
 * Lives in this leaf module (re-exported by the `@/features/flags` barrel) so
 * leaf flag modules can compose it without importing the barrel.
 */
export const useIsProjectsEnabled = (): boolean => {
  const isHeidiIIMasterEnabled = useFeatureFlagEnabled(HEIDI_II_MASTER_FLAG_KEY) ?? false;
  const isHeidi2OptInEnabled = useFeatureFlagEnabled(HEIDI_2_OPT_IN_FLAG_KEY) ?? false;
  // Lite: this getter is read by every Work-gated surface — see `useBooleanFlagLite`.
  const isProjectsFlagEnabled = useBooleanFlagLite(
    projectsFlag.key,
    projectsFlag.defaultValue
  );
  const isProjectsWebEnabled = useBooleanFlagLite(
    projectsWebFlag.key,
    projectsWebFlag.defaultValue
  );
  const heidiVersion = useHeidiVersion();
  const isLegacyProjectsEnabled = isTauri()
    ? isProjectsFlagEnabled
    : isProjectsWebEnabled;
  const isWorkEnabled = isHeidiIIMasterEnabled || isLegacyProjectsEnabled;
  const isHeidi1User = isHeidi2OptInEnabled && heidiVersion === HeidiVersion.Value1;
  return isWorkEnabled && !isHeidi1User;
};

/** Contextual thinking phrases (`turn.loader_phrases`). No dedicated flag. */
export const useIsProjectsContextualLoaderPhrasesEnabled = (): boolean =>
  useIsProjectsEnabled();

/**
 * Agent-driven session recording — the `ui.session_recording` event dispatcher,
 * the global recorder overlay and the artifact-header controls. No dedicated flag.
 */
export const useIsAgentSessionRecordingEnabled = (): boolean => useIsProjectsEnabled();

/** Routines/Workflows UI (BE `is_workflows_enabled` mirrors). */
export const useIsWorkflowsEnabled = (): boolean => useIsProjectsEnabled();

/** Session routine runs. The legacy child key is not read. */
export const useIsSessionRoutineRunsEnabled = (): boolean => useIsProjectsEnabled();

/** Session V2 "Heidi suggests" card: Work umbrella AND `release-scribe-v2-heidi-suggests`. */
export const useIsV2HeidiSuggestsEnabled = (): boolean => {
  const isProjectsEnabled = useIsProjectsEnabled();
  const isHeidiSuggestsFlagEnabled = useIsV2HeidiSuggestsFlagEnabled();
  return isProjectsEnabled && isHeidiSuggestsFlagEnabled;
};

/**
 * Sidebar IA (MUL-3702/3703): primary nav reduction, persistent More, footer rail.
 * Follows `useIsProjectsEnabled`, so Home cannot render for a user who lands on
 * Evidence (`/scribe/chat`) instead of Work (`/scribe/chat/projects`).
 */
export const useIsSidebarIAEnabled = (): boolean => {
  const isProjectsEnabled = useIsProjectsEnabled();
  const isSidebarIAEnabled = useFeatureFlagEnabled(SIDEBAR_IA_FLAG_KEY) ?? false;
  return isProjectsEnabled && isSidebarIAEnabled;
};

export const useIsWorkLetterArtifactEnabled = (): boolean => {
  const isProjectsEnabled = useIsProjectsEnabled();
  const isWorkLetterArtifactEnabled = useBooleanFlag(
    workLetterArtifactFlag.key,
    workLetterArtifactFlag.defaultValue
  );
  return isProjectsEnabled && isWorkLetterArtifactEnabled;
};
